<script setup lang="ts">
import _ from 'lodash';
import { ref, onMounted, computed, watch } from 'vue';
import { useI18n } from 'vue-i18n';
import SvgIcon from '@telecom/shared/components/SvgIcon.vue';
import '@patsnap-ui/icon/assets/solid/export.svg';
import '@patsnap-ui/icon/assets/solid/stop.svg';
import '@patsnap-ui/icon/assets/solid/ArrowLeft.svg';
import '@patsnap-ui/icon/assets/solid/ArrowRight.svg';
import '@/presentation/assets/images/icons/icon-regenerate.svg';
import { StreamStatus } from '../enums';
import { useCommands } from '@telecom/shared/composables/use-commands';
import { useScroll2bottom } from '@telecom/shared/composables/use-scroll2bottom';
import wordsCount from 'words-count';


const { executeCommand, commands } = useCommands();

const props = defineProps<{
    floatingStyles: { [key: string]: string };
    isSelection: boolean;
    commandId: string;
    addTypeList: string[];
    placeholder?: string;
}>();
const CONTENT_CACHE_MAX_NUMBER = 5;
const PREV_CONTENT = 'prev';
const NEXT_CONTENT = 'next';

const paragraphEditRef = ref(null);
const cacheContents = ref<string[]>(['']);
const currentContentIndex = ref(1);
const isGenerating = ref(false);

const command = computed(() => {
    return _.find(commands.getAllCommands(), { id: props.commandId });
});

const currentContent = computed(() => cacheContents.value[currentContentIndex.value - 1] || '');
const currentWordCount = computed(() => {
    const text = currentContent.value || '';
    if (!text) return 0;
    return wordsCount(text);
});

function generateContent() {
    if (cacheContents.value.length <= CONTENT_CACHE_MAX_NUMBER) {
        isGenerating.value = true;
        executeCommand(props.commandId).then(res => {
            res.getContentOpt((status: StreamStatus, content?: string) => {
                cacheContents.value = [...cacheContents.value];
                if (status === StreamStatus.PROCESSING) {
                    if (content) {
                        cacheContents.value[currentContentIndex.value - 1] = content;
                    }
                } else {
                    isGenerating.value = false;
                }
            });
        });
    }
}

const emits = defineEmits<{
    (event: 'close'): void;
    (event: 'cancel'): void;
    (event: 'updateContent', newText: string): void;
    (event: 'replace', newText: string): void;
    (event: 'addition', newText: string): void;
}>();

const { t } = useI18n();

const isAddition = computed(() => {
    return props.addTypeList.some(item => item === props.commandId);
});

function close() {
    stopGenerate();
    emits('close');
}

function stopGenerate() {
    executeCommand(props.commandId).then(res => {
        res.stopGenerate();
    });
    isGenerating.value = false;
}

function handleRegenerate() {
    if (cacheContents.value.length < CONTENT_CACHE_MAX_NUMBER) {
        currentContentIndex.value += 1;
        cacheContents.value.push('');

        generateContent();
    }
}

function handleSubmit() {
    if (isAddition.value) {
        emits('addition', cacheContents.value[currentContentIndex.value - 1]);
    } else {
        emits('replace', cacheContents.value[currentContentIndex.value - 1]);
    }
}

function handleChangeCurrentContentIndex(type: string | undefined) {
    if (isGenerating.value) return;
    if (type === PREV_CONTENT) {
        currentContentIndex.value = Math.max(1, currentContentIndex.value - 1);
    } else {
        currentContentIndex.value = Math.min(cacheContents.value.length, currentContentIndex.value + 1);
    }
}

function init() {
    generateContent();
}

// 生成文本的时候始终保持在最底部
const commentTextarea = ref<HTMLElement | null>(null);
const { scroll2bottomWithLimit } = useScroll2bottom(commentTextarea);

watch(() => cacheContents.value[currentContentIndex.value - 1], () => {
    scroll2bottomWithLimit();
});

onMounted(() => {
    init();
});

</script>


<template>
    <div ref="paragraphEditRef">
        <div class="flex items-center justify-between">
            <div class="flex items-center gap-2">
                <div class="flex items-center gap-1">
                    <SvgIcon
                        class="h-6 w-6 cursor-pointer fill-grey-450"
                        icon="SolidArrowLeft"
                        :class="{ '!cursor-not-allowed !fill-grey-90': isGenerating }"
                        @click="handleChangeCurrentContentIndex(PREV_CONTENT)"
                    />
                    <span class="text-sm text-grey-90">{{ currentContentIndex }}/{{ cacheContents.length }}</span>
                    <SvgIcon
                        class="mr-1 h-6 w-6 cursor-pointer fill-grey-450"
                        icon="SolidArrowRight"
                        :class="{ '!cursor-not-allowed !fill-grey-90': isGenerating }"
                        @click="handleChangeCurrentContentIndex(NEXT_CONTENT)"
                    />
                </div>
                <div class="flex items-center gap-1 p-1">
                    <SvgIcon :icon="command.icon" class="h-6 w-6 !fill-gray-450" />

                    <span class="text-sm text-black">
                        {{
                            command.label
                        }}
                    </span>

                    <span v-if="isSelection">
                        ({{ t('aiDisclosure.forSelectedText') }})
                    </span>
                </div>
            </div>

            <button v-if="isGenerating" class="th-btn th-btn-outline pl-2" @click="stopGenerate">
                <SvgIcon
                    class="th-btn-icon h-6 w-6 !fill-grey-450"
                    icon="SolidStop"
                />

                <span>
                    {{ t('aiDisclosureGlobal.globalBtn.stop') }}
                </span>
            </button>

            <button v-else class="th-btn th-btn-outline pl-2" @click="handleRegenerate">
                <SvgIcon
                    class="th-btn-icon h-6 w-6 !fill-grey-450"
                    icon="IconRegenerate"
                />

                <span>
                    {{ t('aiDisclosureGlobal.globalBtn.regenerate') }}
                </span>
            </button>
        </div>

        <slot name="prepend" :generating="isGenerating" />

        <div class="my-4 relative">
            <textarea
                ref="commentTextarea"
                v-model="cacheContents[currentContentIndex - 1]"
                class="h-[164px] w-full resize-none rounded-lg border border-grey-40 p-3 text-black"
                :placeholder="placeholder"
            />
            <!-- 字数统计，绝对定位在输入框右下角，仅生成完毕时显示 -->
            <span
                v-if="!isGenerating"
                class="absolute right-6 bottom-4 text-xs text-gray-500 pointer-events-none select-none"
            >
                {{ currentWordCount }}
            </span>
        </div>

        <div class="flex items-center justify-between">
            <slot name="feedback" />

            <span class="flex items-center gap-2">
                <button class="th-btn th-btn-secondary" @click="close">
                    {{
                        t('aiDisclosureGlobal.globalBtn.cancel')
                    }}
                </button>

                <button
                    class="th-btn-primary th-btn"
                    :disabled="isGenerating"
                    @click="handleSubmit"
                >
                    {{
                        isAddition ? t('aiDisclosureGlobal.globalBtn.submit') : t('aiDisclosureGlobal.globalBtn.replace')
                    }}
                </button>
            </span>
        </div>
    </div>
</template>

<style lang="scss">
.floating-disclosure-content-edit {
    textarea {
        &:focus-visible {
            outline: none !important;
            border-color: #0764E9;
        }
    }
}
</style>

